import React, { useState, useRef } from 'react';
import { useMedia } from '../../hooks/useMedia';
import './MediaUpload.css';

interface MediaUploadProps {
  imovelId: string;
  onUploadSuccess?: (midias: any[]) => void;
  maxFiles?: number;
  acceptedTypes?: string[];
}

export const MediaUpload: React.FC<MediaUploadProps> = ({
  imovelId,
  onUploadSuccess,
  maxFiles = 10,
  acceptedTypes = ['image/*', 'video/*'],
}) => {
  const { uploadFiles, uploading, error } = useMedia();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      const isValidType = acceptedTypes.some(type => {
        if (type.endsWith('/*')) {
          return file.type.startsWith(type.replace('/*', '/'));
        }
        return file.type === type;
      });

      if (!isValidType) {
        alert(`Arquivo ${file.name} não é um tipo válido`);
        return false;
      }

      if (file.size > 50 * 1024 * 1024) { // 50MB
        alert(`Arquivo ${file.name} é muito grande (máximo 50MB)`);
        return false;
      }

      return true;
    });

    const totalFiles = selectedFiles.length + validFiles.length;
    if (totalFiles > maxFiles) {
      alert(`Máximo de ${maxFiles} arquivos permitidos`);
      return;
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    const result = await uploadFiles(imovelId, selectedFiles);
    
    if (result.success) {
      setSelectedFiles([]);
      onUploadSuccess?.(result.data?.uploaded || []);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFilePreview = (file: File) => {
    if (file.type.startsWith('image/')) {
      return (
        <div className="file-preview image">
          <img
            src={URL.createObjectURL(file)}
            alt={file.name}
            className="preview-image"
          />
          <div className="file-info">
            <span className="file-name">{file.name}</span>
            <span className="file-size">{formatFileSize(file.size)}</span>
          </div>
        </div>
      );
    } else if (file.type.startsWith('video/')) {
      return (
        <div className="file-preview video">
          <div className="video-icon">🎥</div>
          <div className="file-info">
            <span className="file-name">{file.name}</span>
            <span className="file-size">{formatFileSize(file.size)}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="file-preview unknown">
        <div className="file-icon">📄</div>
        <div className="file-info">
          <span className="file-name">{file.name}</span>
          <span className="file-size">{formatFileSize(file.size)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="media-upload">
      <div className="upload-header">
        <h3>Upload de Mídias</h3>
        <p>Adicione fotos e vídeos do imóvel (máximo {maxFiles} arquivos, 50MB cada)</p>
      </div>

      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}

      <div
        className={`upload-dropzone ${dragActive ? 'drag-active' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="dropzone-content">
          <div className="dropzone-icon">📁</div>
          <h4>Arraste arquivos aqui ou clique para selecionar</h4>
          <p>Formatos aceitos: JPG, PNG, GIF, WEBP, MP4, AVI, MOV</p>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileInputChange}
          className="file-input"
        />
      </div>

      {selectedFiles.length > 0 && (
        <div className="selected-files">
          <div className="files-header">
            <h4>Arquivos Selecionados ({selectedFiles.length})</h4>
            <button
              onClick={() => setSelectedFiles([])}
              className="clear-files-btn"
              disabled={uploading}
            >
              Limpar Todos
            </button>
          </div>

          <div className="files-list">
            {selectedFiles.map((file, index) => (
              <div key={index} className="file-item">
                {getFilePreview(file)}
                <button
                  onClick={() => removeFile(index)}
                  className="remove-file-btn"
                  disabled={uploading}
                  title="Remover arquivo"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="upload-actions">
            <button
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
              className="upload-btn"
            >
              {uploading ? (
                <>
                  <div className="upload-spinner"></div>
                  Enviando...
                </>
              ) : (
                <>
                  📤 Enviar {selectedFiles.length} arquivo{selectedFiles.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
